import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlTree } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';

import { AUTH_ROUTES } from '../../auth/constants/auth-routes.constants';
import { ENTITY_ROUTES } from '../../entity/constants/entity-routes.constants';
import { SPONSOR_ROUTES } from '../../sponsor/constants/sponsor-routes.constants';
import { OrganizationType } from '../../auth/interfaces';
import { AuthBootstrapService } from '../services/auth-bootstrap.service';
import { AppRouteNavigationService } from '../services/app-route-navigation.service';
import { AuthService } from '../../auth/services/auth/auth.service';

export function roleMatchGuard(allowedOrganizationTypes: OrganizationType[]): CanMatchFn {
  return (_route, segments): Observable<boolean | UrlTree> => {
    const authService = inject(AuthService);
    const authBootstrap = inject(AuthBootstrapService);
    const router = inject(Router);
    const appRouteNavigation = inject(AppRouteNavigationService);
    const attemptedUrl =
      router.getCurrentNavigation()?.extractedUrl?.toString() ||
      `/${segments.map((segment) => segment.path).join('/')}`;

    return authBootstrap.ensureSessionReady().pipe(
      map((session): boolean | UrlTree => {
        if (authBootstrap.isGuest(session)) {
          const safeRedirect = authService.sanitizePostLoginRedirect(attemptedUrl) ?? attemptedUrl;
          authService.setPostLoginRedirect(safeRedirect);
          return appRouteNavigation.createUrlTree(AUTH_ROUTES.login, {
            queryParams: { redirectTo: safeRedirect },
          });
        }

        if (!authBootstrap.canAccessProtectedRoutes(session)) {
          const safeRedirect = authService.sanitizePostLoginRedirect(attemptedUrl) ?? attemptedUrl;
          authService.setPostLoginRedirect(safeRedirect);
          return appRouteNavigation.createUrlTree(AUTH_ROUTES.login, {
            queryParams: { redirectTo: safeRedirect },
          });
        }

        const currentOrganization = session.organization;
        const isEntityContext =
          currentOrganization?.organization_type === OrganizationType.Entity &&
          allowedOrganizationTypes.includes(OrganizationType.Entity);
        const isSponsorContext =
          currentOrganization?.organization_type === OrganizationType.Sponsor &&
          allowedOrganizationTypes.includes(OrganizationType.Sponsor);
        const hasAllowedRole = isEntityContext || isSponsorContext;

        if (hasAllowedRole) {
          return true;
        }

        if (currentOrganization?.organization_type === OrganizationType.Entity) {
          return appRouteNavigation.createUrlTree(ENTITY_ROUTES.root);
        }
        if (currentOrganization?.organization_type === OrganizationType.Sponsor) {
          return appRouteNavigation.createUrlTree(SPONSOR_ROUTES.root);
        }

        const safeRedirect = authService.sanitizePostLoginRedirect(attemptedUrl) ?? attemptedUrl;
        authService.setPostLoginRedirect(safeRedirect);
        return appRouteNavigation.createUrlTree(AUTH_ROUTES.login, {
          queryParams: { redirectTo: safeRedirect },
        });
      }),
      catchError(() => {
        const safeRedirect = authService.sanitizePostLoginRedirect(attemptedUrl) ?? attemptedUrl;
        return of(
          appRouteNavigation.createUrlTree(AUTH_ROUTES.login, {
            queryParams: { redirectTo: safeRedirect },
          }),
        );
      }),
    );
  };
}
